import { Component } from 'base/js/aem';
import { onIntersect } from 'base/js/templates/general/onIntersect.js';

const hundop = 100;
const minutes = 60;
const jump = 3;
const padding = 2;

class Video extends Component {
    init() {
        this.onEnter = () => {
            this.element.play();
        };

        this.onExit = () => {
            return;
        };
        if (this.element.classList.contains('video-replay-scroll')) {
            onIntersect(this.element, this.onEnter, this.onExit, false, { threshold: 0.7 });
        }
        this.addControls();
    }

    handleEvent(e) {
        this[`handle${e.type}`](e);
    }

    updateDuration() {
        const { element: player, timeLabel } = this;
        let durationMinutes = String(Math.floor(player.duration / minutes)).padStart(padding, '0');
        let durationSeconds = String(Math.floor(player.duration % minutes)).padStart(padding, '0');
        player.dataset.duration = `${durationMinutes}:${durationSeconds}`;
        timeLabel.textContent = `00:00 / ${durationMinutes}:${durationSeconds}`;
    }

    /**
     * Pause event handler.
     */
    handlepause() {
        this.updateDuration();
        const { playPauseBtn, overlayPlay } = this;
        // Update text and styling of pause button.
        playPauseBtn.ariaLabel = playPauseBtn.dataset.playLabel;
        playPauseBtn.classList.remove('fa-pause');
        playPauseBtn.classList.add('fa-play');
        overlayPlay.classList.remove('visually-hidden');
        overlayPlay.ariaLabel = overlayPlay.dataset.playLabel;
    }

    /**
     * Playing event handler.
     * Updates play/pause button and removes play button overlay.
     */
    handleplaying() {
        this.updateDuration();
        const { playPauseBtn, overlayPlay } = this;
        // Update text and styling of pause button.
        playPauseBtn.ariaLabel = playPauseBtn.dataset.pauseLabel;
        playPauseBtn.classList.remove('fa-play');
        playPauseBtn.classList.add('fa-pause');
        overlayPlay.classList.add('visually-hidden');
        overlayPlay.ariaLabel = overlayPlay.dataset.pauseLabel;
    }

    /**
     * Play event handler to update play/pause button.
     */
    handleplay() {
        const { playPauseBtn } = this;
        // Update text and styling of pause button.
        playPauseBtn.ariaLabel = playPauseBtn.dataset.pauseLabel;
        playPauseBtn.classList.remove('fa-play');
        playPauseBtn.classList.add('fa-pause');
    }

    /*
     * Keydown event handler to skip forward/backward.
     */
    handlekeydown(e) {
        const key = e.key;
        const { element: player } = this;
        switch (key) {
        case 'LeftArrow':
            player.currentTime -= jump;
            break;
        case 'RightArrow':
            player.currentTime += jump;
            break;
        case 'Space':
        case 'p':
            this.playPause();
            break;
        default:
            return;
        }
    }

    /*
     * Handle volume change.
     */
    handlechange(e) {
        const volume = e.target.value / hundop;
        const { element: player } = this;
        player.volume = volume;
    }

    /*
     * Handle loaded metadata to update duration.
     */
    handleloadedmetadata() {
        this.updateDuration();
    }

    /*
     * Handle loaded data to update duration.
     */
    handleloadeddata() {
        this.updateDuration();
    }

    /*
     * Time update handler. Updates time label.
     */
    handletimeupdate(e) {
        const { timeLabel, progress, element: player } = this;
        const percentage = player.currentTime / player.duration * hundop;
        const duration = player.dataset.duration ? player.dataset.duration : e.target.duration;
        let playerMinutes = Math.floor(player.currentTime / minutes);
        let playerSeconds = Math.floor(player.currentTime - playerMinutes * minutes);
        progress.value = percentage;
        let mediaTime = `${String(playerMinutes).padStart(padding, '0')}:${String(playerSeconds)
            .padStart(padding, '0')} / ${duration}`;
        timeLabel.textContent = mediaTime;
        timeLabel.ariaLabel = mediaTime;
    }

    /*
     * Generic click handler. Decides what to do based on what element was clicked.
     */
    handleclick(e) {
        // Destructure elements to make code cleaner.
        const { stopBtn, muteButton, rwdBtn, fwdBtn, progress,
            captionButton, overlayPlay, playPauseBtn, element: player } = this;
        e.stopPropagation();
        switch (e.target) {
        case stopBtn:
            this.stopVideo(player);
            break;
        case muteButton:
            this.handleMute(e, player);
            break;
        case rwdBtn:
            this.skipBack(player);
            break;
        case fwdBtn:
            this.skipAhead(player);
            break;
        case progress:
            this.handleProgress(e, player);
            break;
        case captionButton:
            this.handleCaptions(e, player);
            break;
        case overlayPlay:
        case playPauseBtn:
        case player:
            this.playPause();
            break;
        default:
            break;
        }
    }

    /*
     * Play/pause toggle.
     */
    playPause() {
        if (this.element.paused) {
            this.element.play();
        } else {
            this.element.pause();
        }
    }

    /*
     * Stop video by pausing it and setting the currentTime to 0.
     */
    stopVideo(player) {
        player.pause();
        player.currentTime = 0;
    }

    /*
     * Mute/unmute depending on current state of player.
     */
    handleMute(e, player) {
        const classes = e.target.classList;
        const muted = player.muted;
        if (muted) {
            player.muted = false;
            classes.remove('fa-volume-slash');
            classes.add('fa-volume');
        } else {
            player.muted = true;
            classes.remove('fa-volume');
            classes.add('fa-volume-slash');
        }
    }

    /*
     * Rewind button handler. I considered handling this and the fwd button in the same function but this is clearer?
     */
    skipBack(player) {
        if (player) {
            player.currentTime -= jump;
        }
    }

    /*
     * Fwd button handler.
     */
    skipAhead(player) {
        player.currentTime += jump;
        if (player.currentTime >= player.duration) {
            this.stopVideo(player);
        }
    }

    /*
     * Handle click on progress bar.
     */
    handleProgress(e, player) {
        const rect = e.target.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const progressBarWidth = rect.width;
        let percentage = clickX / progressBarWidth * hundop;
        e.currentTarget.value = percentage;
        player.currentTime = player.duration * (percentage / hundop);
    }

    /*
     * Enable/disable captions.
     */
    handleCaptions(e, player) {
        const captionStatus = !e.target.classList.contains('fa-closed-captioning');
        [ ...player.textTracks ].forEach(track => {
            track.mode = captionStatus ? 'showing' : 'hidden';
        });
        e.target.classList.toggle('fa-closed-captioning');
        e.target.classList.toggle('fa-closed-captioning-slash');
    }

    addControls() {
        if (this.element && this.element.controls) {
            this.element.dataset.controls = 'true';
            this.element.removeAttribute('controls');
            this.parent = this.element.parentElement;
            this.controls = this.parent.querySelector('.controls');
            this.playPauseBtn = this.controls.querySelector('.playpause');
            this.stopBtn = this.controls.querySelector('.stop');
            this.rwdBtn = this.controls.querySelector('.rwd');
            this.fwdBtn = this.controls.querySelector('.fwd');
            this.timeLabel = this.controls.querySelector('.time');
            this.progress = this.controls.querySelector('progress');
            this.overlayPlay = this.parent.querySelector('.overlay-play');
            this.volume = this.controls.querySelector('.volume-level input');
            this.muteButton = this.controls.querySelector('.mute');
            this.captionButton = this.element.querySelector('track') ? this.controls.querySelector('.captions') : null;
            // Hide all caption tracks.
            [ ...this.element.textTracks ].forEach(track => {
                track.mode = 'hidden';
            });

            /*
             * This set of event listeners listen for events and dispatch them to this class, retaining the value of
             * 'this'. this.handleEvent is a built-in handler for all events and fires the correct handler depending
             * which event is being fired.
             */
            this.element.addEventListener('pause', this);
            this.element.addEventListener('playing', this);
            this.element.addEventListener('play', this);
            this.parent.addEventListener('keydown', this);
            this.volume.addEventListener('change', this);
            this.element.addEventListener('loadedmetadata', this);
            this.element.addEventListener('loadeddata', this);
            this.element.addEventListener('timeupdate', this);
            this.element.addEventListener('click', this);
            this.controls.addEventListener('click', this);
            this.overlayPlay.addEventListener('click', this);

            /*
            this.playPauseBtn.addEventListener('click', this);
            this.overlayPlay.addEventListener('click', this);
            this.stopBtn.addEventListener('click', this);
            this.muteButton.addEventListener('click', this);
            this.progress.addEventListener('click', this);
            this.rwdBtn.addEventListener('click', this);
            this.fwdBtn.addEventListener('click', this);
            if (this.captionButton) {
                this.captionButton.addEventListener('click', this);
            }

             */
        }
    }
}

export { Video };
